import { Stack, Typography } from '@mui/material';
import { GetEventsQueryFilters, useEventsBulkStatusChangeMutation } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useNotifications } from '@schooly/components/notifications';
import { CheckboxSquareIconButton, SimpleButton, Spin } from '@schooly/style';
import { BulkActionsBottomBar, SelectedIds } from '@schooly/utils/bulk-actions';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useSidebarLayout } from '../../components/uikit-components/MainLayout/MainLayout';

type EventsBulkActionsProps = {
  schoolId: string;
  selectedIds: SelectedIds;
  query?: string;
  filters: GetEventsQueryFilters;
  onClear: () => void;
};

export const EventsBulkActions: FC<EventsBulkActionsProps> = ({
  selectedIds,
  onClear,
  schoolId,
  query,
  filters,
}) => {
  const { formatMessage } = useIntl();
  const { sidebarWidth } = useSidebarLayout();

  const { showNotification, showError } = useNotifications();
  const { getConfirmation } = useConfirmationDialog();
  const eventsStatusChangeMutation = useEventsBulkStatusChangeMutation();

  const onPublishEvents = useCallback(async () => {
    const isConfirmed = await getConfirmation({
      textId: 'events-notification-NotifyAfterAction',
      textValues: { action: formatMessage({ id: 'publishing' }).toLowerCase() },
      confirmTextId: 'yes',
      cancelTextId: 'no',
      cancelOnClose: false,
    });

    if (isConfirmed === undefined) return;

    eventsStatusChangeMutation.mutate(
      selectedIds === 'all'
        ? {
            schoolId,
            query,
            filters,
            notify_parents: isConfirmed,
            action: 'publish',
            withFollowing: false,
          }
        : {
            schoolId,
            event_ids: Array.from(selectedIds),
            notify_parents: isConfirmed,
            action: 'publish',
            withFollowing: false,
          },
      {
        onSuccess: (res) => {
          showNotification({
            textId: 'events-action-PublishSuccess',
            values: { eventsCount: res.event_count },
            type: 'success',
          });
          onClear();
        },
        onError: showError,
      },
    );
  }, [
    getConfirmation,
    formatMessage,
    eventsStatusChangeMutation,
    selectedIds,
    schoolId,
    query,
    filters,
    showError,
    showNotification,
    onClear,
  ]);

  if (selectedIds !== 'all' && !selectedIds.size) return null;

  return (
    <BulkActionsBottomBar
      sx={{ pointerEvents: eventsStatusChangeMutation.isLoading ? 'none' : undefined }}
      sidebarWidth={sidebarWidth}
    >
      <Stack flexDirection="row" alignItems="center" minWidth={240} gap={1}>
        <CheckboxSquareIconButton isActive onClick={onClear} />
        <Typography variant="h3">
          {formatMessage(
            { id: 'countOfTypeSelected' },
            selectedIds === 'all'
              ? {
                  count: formatMessage({ id: 'filter-All' }),
                  userTypePlural: formatMessage({ id: 'event-type-plural' }).toLowerCase(),
                }
              : {
                  count: selectedIds.size,
                  userTypePlural: formatMessage({
                    id: selectedIds.size > 1 ? 'event-type-plural' : 'event-type',
                  }).toLowerCase(),
                },
          )}
        </Typography>
      </Stack>
      <SimpleButton
        startIcon={eventsStatusChangeMutation.isLoading ? <Spin /> : undefined}
        disabled={eventsStatusChangeMutation.isLoading}
        onClick={onPublishEvents}
      >
        {formatMessage({
          id: 'events-action-Publish',
        })}
      </SimpleButton>
    </BulkActionsBottomBar>
  );
};
